import React, { createContext, useReducer, ReactNode, useEffect, useMemo } from 'react';
import { appStateReducer } from './AppReducer';
import { historyList, historyEnsure, getFAQs } from '../api';
import { ChatHistoryLoadingState, DynamoDBStatus } from '../constants/enums';
import { useSessionCookie } from '../components/authentication/UseSessionCookie';
import { COOKIE_NAME_LOGIN_ROLE } from '../constants/config';
import { getAgentsConfigs } from '../api/system';

export interface AppState {
    isChatHistoryOpen: boolean;
    chatHistoryLoadingState: ChatHistoryLoadingState;
    isDynamoDBAvailable: DynamoDBHealth;
    chatHistory: Conversation[] | null;
    filteredChatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    refreshSearchHistoryCounter: number;
    loggedInAsRole: string | undefined;
    searchInputValue: string;
    isDarkMode: boolean;
    screenSize: number;
    selectedSearchResult: SearchResult | undefined;
    chFilter: CHFilter;
    agentOptions: Agent[];
    selectedAgent: Agent | null;
    faqList: string[];
}

export type Action =
    | { type: 'TOGGLE_CHAT_HISTORY' }
    | { type: 'SET_COSMOSDB_STATUS', payload: DynamoDBHealth }
    | { type: 'SET_SELECTED_SEARCHRESULT', payload: SearchResult | undefined }
    | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState }
    | { type: 'UPDATE_CURRENT_CHAT', payload: Conversation | null }
    | { type: 'UPDATE_FILTERED_CHAT_HISTORY', payload: Conversation[] | null }
    | { type: 'UPDATE_CHAT_HISTORY', payload: Conversation } // API Call
    | { type: 'UPDATE_CHAT_TITLE', payload: Conversation } // API Call
    | { type: 'DELETE_CHAT_ENTRY', payload: string } // API Call
    | { type: 'DELETE_CHAT_HISTORY' }  // API Call
    | { type: 'DELETE_CURRENT_CHAT_MESSAGES', payload: string }  // API Call
    | { type: 'FETCH_CHAT_HISTORY', payload: Conversation[] | null }  // API Call
    | { type: 'SET_USER_LOGIN_STATUS', payload: string | undefined }
    | { type: 'SET_REFRESH_SEARCH_HISTORY', payload: number }
    | { type: 'UPDATE_SEARCH_QUERY', payload: string }
    | { type: 'TOGGLE_DARK_MODE' }
    | { type: 'SCREEN_SIZE', payload: number }
    | { type: 'UPDATE_CH_FILTER', payload: CHFilter }
    | { type: 'SET_AGENT_OPTIONS', payload: Agent[] }
    | { type: 'SET_SELECTED_AGENT', payload: Agent | null }
    | { type: 'SET_FAQ_LIST', payload: string[] };




export const AppStateContext = createContext<{
    state: AppState;
    dispatch: React.Dispatch<Action>;
} | undefined>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
};

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [loggedInAsRole] = useSessionCookie(COOKIE_NAME_LOGIN_ROLE, undefined);

    const initialState: AppState = {
        isChatHistoryOpen: false,
        chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
        chatHistory: null,
        filteredChatHistory: null,
        currentChat: null,
        isDynamoDBAvailable: {
            dynamoDB: false,
            status: DynamoDBStatus.NotConfigured,
        },
        refreshSearchHistoryCounter: 0,
        loggedInAsRole: loggedInAsRole,
        searchInputValue: '',
        isDarkMode: localStorage.getItem('darkmode') === '1',
        screenSize: 3,
        selectedSearchResult: undefined,
        chFilter: {
            category_options: [],
            date_filter: {
                start_date: null,
                end_date: null,
            },
        },
        agentOptions: [],
        selectedAgent: null,
        faqList: [],
    };

    const [state, dispatch] = useReducer(appStateReducer, initialState);
    //set default agent
    useEffect(() => {
        getAgentsConfigs().then((agents) => {
            // console.log("agents: ", agents)
            if (agents) {
                dispatch({ type: 'SET_AGENT_OPTIONS', payload: agents });
                if(agents.length > 0){
                    dispatch({ type: 'SET_SELECTED_AGENT', payload: agents[0] });
                }
            }
        })
    },[])

    useEffect(() => {
        if(state.selectedAgent == null) return
        getFAQs(state.selectedAgent.id).then((faqs) => {
            if (faqs) {
                dispatch({ type: 'SET_FAQ_LIST', payload: faqs.queries });
            }
        }).catch((error) => {
            console.error("Error fetching FAQs:", error);
        });
        
    },[state.selectedAgent])
    useEffect(() => {

        if (!state.loggedInAsRole) return;

        // Check for cosmosdb config and fetch initial data here
        const fetchChatHistory = async (): Promise<Conversation[] | null> => {
            const result = await historyList().then((response) => {
                if (response) {
                    dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response });
                } else {
                    dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
                }
                return response
            })
                .catch((err) => {
                    dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
                    console.error("There was an issue fetching your data.");
                    return null
                })
            return result
        };

        const getHistoryEnsure = async () => {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading });
            historyEnsure().then((response) => {
                if (response?.dynamoDB) {
                    fetchChatHistory()
                        .then((res) => {
                            if (res) {
                                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success });
                                dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                            } else {
                                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                                dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { dynamoDB: false, status: DynamoDBStatus.NotWorking } });
                            }
                        })
                        .catch((err) => {
                            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                            dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { dynamoDB: false, status: DynamoDBStatus.NotWorking } });
                        })
                } else {
                    dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                }
            }).catch((err) => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: 'SET_COSMOSDB_STATUS', payload: { dynamoDB: false, status: DynamoDBStatus.NotConfigured } });
            })
        }
        getHistoryEnsure();
    }, [state.loggedInAsRole, state.refreshSearchHistoryCounter]);

    const updatedState = useMemo(() => ({ state, dispatch }), [state]);

    return (
        <AppStateContext.Provider value={updatedState}>
            {children}
        </AppStateContext.Provider>
    );
};


